import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronRight, Plus } from 'lucide-react'
import { format, parseISO, differenceInDays } from 'date-fns'
import { useAuthStore } from '@/store/authStore'
import {
  useEntries, useEntryStats, useCapsules, useCreateCapsule,
  useVaultDocuments, useLegacyContacts, usePublicProfiles,
  useInformPosts, useEntriesOnDate,
} from '@/hooks/useData'

// ─── DiscoverPage ─────────────────────────────────────────
export function DiscoverPage() {
  const { data: profiles } = usePublicProfiles()
  return (
    <div className="flex flex-col min-h-full">
      <div className="glass sticky top-0 z-20 px-5 pt-14 pb-4 safe-top">
        <h1 className="font-serif text-2xl font-bold text-white">Discover</h1>
        <div className="flex items-center gap-2 mt-3 bg-white/5 border border-white/[0.07] rounded-xl px-3 py-2.5">
          <span className="text-white/30">🔍</span>
          <span className="text-white/30 text-sm">Search timelines, people, tags…</span>
        </div>
      </div>
      <div className="px-4 py-4">
        <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-3">Public Timelines</p>
        <div className="grid grid-cols-2 gap-3">
          {profiles?.map(p => (
            <div key={p.id} className="card p-3 cursor-pointer">
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-serif text-lg text-white font-bold mb-2"
                style={{background:'linear-gradient(135deg,#4f6ef7,#8b5cf6)'}}>
                {p.full_name?.[0] ?? '?'}
              </div>
              <p className="font-semibold text-white text-sm">{p.full_name}</p>
              <p className="font-mono text-[10px] text-white/30">@{p.username}</p>
              {p.location && <p className="text-xs text-white/30 mt-1">📍 {p.location}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── NotificationsPage ────────────────────────────────────
export function NotificationsPage() {
  const NOTIFS = [
    { id:'1', icon:'📅', title:'On This Day — 3 years ago', body:'You hiked your first 14er. Timpanogos.', time:'8:00 AM' },
    { id:'2', icon:'⏳', title:'Capsule Countdown', body:'"Letter to Emma" unlocks in 2,290 days.', time:'Yesterday' },
    { id:'3', icon:'👤', title:'New follower', body:'@sarah_j started following your timeline.', time:'2 days ago' },
    { id:'4', icon:'🔐', title:'Vault reminder', body:"Your will document hasn't been updated in 12 months.", time:'3 days ago' },
  ]
  return (
    <div className="flex flex-col min-h-full">
      <div className="glass sticky top-0 z-20 px-5 pt-14 pb-4 safe-top">
        <h1 className="font-serif text-2xl font-bold text-white">Notifications</h1>
      </div>
      <div className="flex flex-col divide-y divide-white/[0.05]">
        {NOTIFS.map(n => (
          <div key={n.id} className="flex gap-3 px-5 py-4 cursor-pointer">
            <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-xl flex-shrink-0">{n.icon}</div>
            <div className="flex-1">
              <p className="text-white font-semibold text-sm">{n.title}</p>
              <p className="text-white/40 text-sm mt-0.5">{n.body}</p>
            </div>
            <span className="font-mono text-[10px] text-white/20 whitespace-nowrap pt-0.5">{n.time}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── ProfilePage ──────────────────────────────────────────
export function ProfilePage() {
  const { profile } = useAuthStore()
  const { data: stats } = useEntryStats()
  return (
    <div className="flex flex-col min-h-full">
      <div className="glass sticky top-0 z-20 px-5 pt-14 pb-4 safe-top flex justify-between items-center">
        <h1 className="font-serif text-2xl font-bold text-white">Profile</h1>
        <Link to="/settings" className="text-white/40 text-sm font-mono">Settings</Link>
      </div>
      <div className="flex flex-col items-center gap-4 px-5 pt-8 pb-4">
        <div className="w-24 h-24 rounded-full flex items-center justify-center font-serif text-4xl text-white font-bold"
          style={{background:'linear-gradient(135deg,#4f6ef7,#8b5cf6)'}}>
          {profile?.full_name?.[0] ?? '?'}
        </div>
        <div className="text-center">
          <h2 className="font-serif text-2xl font-bold text-white">{profile?.full_name}</h2>
          <p className="font-mono text-sm text-white/30">@{profile?.username}</p>
          {profile?.bio && <p className="text-white/50 text-sm mt-2 max-w-xs leading-relaxed">{profile.bio}</p>}
          {profile?.location && <p className="text-white/30 text-sm mt-1">📍 {profile.location}</p>}
        </div>
        <div className="flex gap-8">
          {([['Memories', stats?.total ?? 0], ['Years', stats?.years.length ?? 0], ['Milestones', stats?.milestones ?? 0]] as [string, number][]).map(([label, val]) => (
            <div key={label} className="text-center">
              <div className="font-serif text-3xl font-bold text-white">{val}</div>
              <div className="font-mono text-[10px] text-white/30 uppercase tracking-widest">{label}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="px-4 flex flex-col gap-3 pb-8">
        {[
          { to:'/timeline/chronological', icon:'📅', title:'Chronological View', desc:'Your signature timeline view' },
          { to:'/vault', icon:'🔐', title:'Legal Vault', desc:'Documents & permissions' },
          { to:'/legacy', icon:'🕯️', title:'Legacy Mode', desc:'Your story outlives you' },
          { to:'/summary', icon:'🔍', title:'Day Summary', desc:'What happened on this day' },
        ].map(item => (
          <Link key={item.to} to={item.to} className="card p-4 flex items-center gap-3">
            <span className="text-2xl">{item.icon}</span>
            <div className="flex-1">
              <p className="font-semibold text-white">{item.title}</p>
              <p className="text-xs text-white/40">{item.desc}</p>
            </div>
            <ChevronRight size={16} className="text-white/30" />
          </Link>
        ))}
      </div>
    </div>
  )
}

// ─── SummaryPage ──────────────────────────────────────────
export function SummaryPage() {
  const [month, setMonth] = useState(new Date().getMonth() + 1)
  const [day, setDay] = useState(new Date().getDate())
  const [searched, setSearched] = useState(false)
  const { data: entries, refetch } = useEntriesOnDate(month, day)
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return (
    <div className="flex flex-col min-h-full">
      <div className="glass sticky top-0 z-20 px-5 pt-14 pb-4 safe-top">
        <h1 className="font-serif text-2xl font-bold text-white">Day Summary</h1>
        <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mt-0.5">What happened on this day across all years</p>
        <div className="flex gap-2 mt-3">
          <select value={month} onChange={e => setMonth(+e.target.value)} className="flex-1 input-field text-sm">
            {MONTHS.map((m,i) => <option key={m} value={i+1}>{m}</option>)}
          </select>
          <input type="number" min={1} max={31} value={day}
            onChange={e => setDay(+e.target.value)} className="w-20 input-field text-sm text-center" />
          <button onClick={() => { refetch(); setSearched(true) }}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white"
            style={{background:'linear-gradient(135deg,#4f6ef7,#8b5cf6)'}}>Search</button>
        </div>
      </div>
      <div className="px-4 py-4 flex flex-col gap-3">
        {searched && entries && (entries.length === 0 ? (
          <div className="flex flex-col items-center py-12 gap-3">
            <div className="text-4xl">🗓</div>
            <p className="text-white/40 text-center font-serif text-lg">No entries on {MONTHS[month-1]} {day} yet.</p>
            <p className="text-white/30 text-sm text-center">Add a memory to start building your "On This Day" history.</p>
          </div>
        ) : entries.map(e => (
          <div key={e.id} className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-xs text-white/30">{new Date(e.recorded_at).getFullYear()}</span>
              {e.is_milestone && <span className="text-xs text-yellow-400">🏆 Milestone</span>}
            </div>
            <h3 className="font-semibold text-white">{e.title}</h3>
            {e.body && <p className="text-white/50 text-sm mt-1 line-clamp-2">{e.body}</p>}
            {e.location_name && <p className="text-white/30 text-xs mt-2">📍 {e.location_name}</p>}
          </div>
        )))}
      </div>
    </div>
  )
}

// ─── VaultPage ────────────────────────────────────────────
export function VaultPage() {
  const { data: docs } = useVaultDocuments()
  const DOC_ICONS: Record<string, string> = {
    will:'📋', medical_directive:'🏥', property_deed:'🏠',
    insurance:'📄', letter:'💌', other:'📁',
  }
  return (
    <div className="flex flex-col min-h-full">
      <div className="glass sticky top-0 z-20 px-5 pt-14 pb-4 safe-top flex justify-between items-center">
        <div>
          <h1 className="font-serif text-2xl font-bold text-white">Legal Vault</h1>
          <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mt-0.5">🔒 End-to-end encrypted</p>
        </div>
        <button className="px-3.5 py-2 rounded-xl text-sm font-semibold text-white"
          style={{background:'linear-gradient(135deg,#4f6ef7,#8b5cf6)'}}>+ Add</button>
      </div>
      <div className="px-4 py-4 flex flex-col gap-3">
        {!docs?.length ? (
          <div className="flex flex-col items-center py-16 gap-4">
            <div className="text-5xl">🔐</div>
            <p className="font-serif text-xl text-white/40 text-center">Your vault is empty.</p>
            <p className="text-white/30 text-sm text-center max-w-xs leading-relaxed">
              Store wills, medical directives, property deeds, and personal letters — each with granular release permissions.
            </p>
          </div>
        ) : docs.map(doc => (
          <div key={doc.id} className={`card p-4 ${doc.is_sealed ? 'border-yellow-500/25' : ''}`}>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/[0.07] flex items-center justify-center text-2xl">
                {DOC_ICONS[doc.doc_type] ?? '📁'}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-white">{doc.title}</p>
                <p className="font-mono text-xs text-white/30 capitalize">{doc.doc_type.replace('_', ' ')}</p>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full font-mono border
                ${doc.is_sealed ? 'border-yellow-500/30 text-yellow-400 bg-yellow-500/10' : 'border-green-500/30 text-green-400 bg-green-500/10'}`}>
                {doc.is_sealed ? '🔒 Sealed' : '✓ Stored'}
              </span>
            </div>
            {doc.vault_permissions?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/[0.07]">
                <p className="font-mono text-[9px] text-white/20 tracking-widest uppercase mb-2">Permissions</p>
                {doc.vault_permissions.map((p: any) => (
                  <div key={p.id} className="flex items-center gap-2 py-1">
                    <div className="w-6 h-6 rounded-full bg-blue/20 flex items-center justify-center text-xs font-bold"
                      style={{color:'#7b96ff'}}>{p.grantee_name[0]}</div>
                    <span className="text-xs text-white/50 flex-1">{p.grantee_name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-white/30 font-mono capitalize">
                      {p.permission_level.replace('_', ' ')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── LegacyPage ──────────────────────────────────────────
export function LegacyPage() {
  const { data: contacts } = useLegacyContacts()
  return (
    <div className="flex flex-col min-h-full" style={{background:'linear-gradient(180deg,#0d0a1a 0%,#130f24 100%)'}}>
      <div className="px-5 pt-14 pb-6 safe-top">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3"
          style={{background:'rgba(139,92,246,0.12)',border:'1px solid rgba(139,92,246,0.25)'}}>
          <span className="font-mono text-[10px] tracking-widest uppercase" style={{color:'#8b5cf6'}}>🕯 Legacy Mode</span>
        </div>
        <h1 className="font-serif text-3xl font-bold text-white leading-tight mb-2">Your story<br />outlives you.</h1>
        <p className="text-white/40 text-sm leading-relaxed">Designate trusted contacts, set release triggers, and ensure your memories reach the right people at the right time.</p>
      </div>
      <div className="px-4 flex flex-col gap-3 pb-8">
        {[
          { icon:'🔑', title:'Account Access Transfer', desc:'Designate who receives full account access.' },
          { icon:'🌹', title:'Memorial Timeline', desc:'Convert your account to a read-only memorial.' },
          { icon:'📦', title:'Private Vault Release', desc:'Sealed entries released to named recipients.' },
          { icon:'📖', title:'Auto Legacy Book', desc:'Compiled and shipped to your family.' },
        ].map(item => (
          <div key={item.icon} className="card p-4 flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center text-2xl flex-shrink-0">{item.icon}</div>
            <div>
              <p className="font-semibold text-white">{item.title}</p>
              <p className="text-xs text-white/40 mt-0.5">{item.desc}</p>
            </div>
          </div>
        ))}
        <div className="card p-4 mt-2">
          <p className="font-mono text-[10px] text-white/30 tracking-widest uppercase mb-3">Trusted Contacts</p>
          {!contacts?.length ? (
            <p className="text-white/30 text-sm">No contacts added yet.</p>
          ) : contacts.map(c => (
            <div key={c.id} className="flex items-center gap-3 py-2">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm text-white font-bold"
                style={{background:'rgba(139,92,246,0.3)'}}>{c.contact_name[0]}</div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium">{c.contact_name}</p>
                <p className="font-mono text-[10px] text-white/30 capitalize">{c.release_trigger.replace('_',' ')}</p>
              </div>
              <span className="text-green-400 text-xs font-mono">✓ Set</span>
            </div>
          ))}
          <button className="btn-secondary mt-3 text-sm">+ Add Trusted Contact</button>
        </div>
        <button className="btn-primary mt-2">🔒 Activate Legacy Mode — $79</button>
      </div>
    </div>
  )
}

// ─── MemorialPage ─────────────────────────────────────────
export function MemorialPage() {
  const { profile } = useAuthStore()
  const { data: stats } = useEntryStats()
  return (
    <div className="min-h-dvh flex flex-col items-center" style={{background:'linear-gradient(180deg,#050810,#08060f)'}}>
      <div className="w-full max-w-lg px-6 py-16 flex flex-col items-center gap-6">
        <div className="relative">
          <div className="absolute inset-0 rounded-full"
            style={{background:'radial-gradient(circle,rgba(139,92,246,0.2),transparent 70%)',transform:'scale(1.5)',animation:'pulse 3s infinite'}} />
          <div className="w-24 h-24 rounded-full flex items-center justify-center font-serif text-4xl text-white font-bold relative z-10 border border-white/10"
            style={{background:'linear-gradient(135deg,#1a1f4e,#2d1060)'}}>
            {profile?.full_name?.[0] ?? 'D'}
          </div>
        </div>
        <div className="text-center">
          <h1 className="font-serif text-4xl font-bold text-white mb-2">{profile?.full_name ?? 'Daniel Forbush'}</h1>
          <p className="font-mono text-sm text-white/25 tracking-widest">1992 — ∞</p>
          <p className="font-serif text-lg italic text-white/40 mt-4 max-w-sm leading-relaxed">
            {profile?.memorial_tagline ?? '"A husband. A father. A builder of things."'}
          </p>
        </div>
        <div className="flex gap-8">
          {([['Memories', stats?.total ?? 147],['Years', stats?.years.length ?? 8],['Milestones', stats?.milestones ?? 12]] as [string,number][]).map(([l,v]) => (
            <div key={l} className="text-center">
              <div className="font-serif text-3xl font-bold text-white/80">{v}</div>
              <div className="font-mono text-[9px] text-white/20 uppercase tracking-widest">{l}</div>
            </div>
          ))}
        </div>
        <div className="w-full bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5">
          <p className="font-mono text-[9px] text-white/20 tracking-widest uppercase mb-3">Life Summary · AI Generated</p>
          <p className="font-serif text-base text-white/55 italic leading-relaxed">
            A devoted husband and present father, {profile?.full_name ?? 'Daniel'} documented life with unusual intention — believing ordinary days were worth preserving. They leave behind a timeline, not of accomplishments, but of a life fully lived, honestly recorded, and lovingly preserved.
          </p>
        </div>
        <button className="btn-primary max-w-xs">📖 Download Full Life Summary</button>
      </div>
    </div>
  )
}

// ─── InformPage ──────────────────────────────────────────
export function InformPage() {
  const [category, setCategory] = useState('')
  const { data: posts } = useInformPosts(category || undefined)
  const CATS = [
    { value:'', label:'✦ For You' },
    { value:'news', label:'📰 News' },
    { value:'blog', label:'📖 Blogs' },
    { value:'journal', label:'📓 Journals' },
    { value:'report', label:'📊 Reports' },
    { value:'research', label:'🔬 Research' },
  ]
  return (
    <div className="flex flex-col min-h-full">
      <div className="glass sticky top-0 z-20 px-5 pt-14 pb-3 safe-top">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="font-serif text-2xl font-bold text-white">Inform</h1>
            <p className="font-mono text-[10px] tracking-widest mt-0.5" style={{color:'#2dd4bf'}}>📍 Local + World</p>
          </div>
          <button className="px-3 py-1.5 rounded-xl text-sm font-semibold text-white"
            style={{background:'linear-gradient(135deg,#4f6ef7,#8b5cf6)'}}>✍️ Write</button>
        </div>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
          {CATS.map(c => (
            <button key={c.value} onClick={() => setCategory(c.value)}
              className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all
                ${category===c.value ? 'border-blue text-blue-light bg-blue/10' : 'border-white/[0.07] text-white/40 bg-white/5'}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-col divide-y divide-white/[0.05]">
        {!posts?.length ? (
          <div className="flex flex-col items-center py-16 gap-4">
            <div className="text-5xl">📰</div>
            <p className="font-serif text-xl text-white/40">No posts yet.</p>
            <p className="text-white/30 text-sm text-center max-w-xs">Be the first to write something for your community.</p>
          </div>
        ) : posts.map((post: any) => (
          <div key={post.id} className="flex gap-3 px-5 py-4 cursor-pointer hover:bg-white/[0.02]">
            <div className="w-16 h-16 rounded-xl bg-white/5 flex-shrink-0 flex items-center justify-center text-2xl overflow-hidden">
              {post.cover_url ? <img src={post.cover_url} className="w-full h-full object-cover" alt="" /> : '📄'}
            </div>
            <div className="flex-1">
              <p className="font-mono text-[10px] uppercase tracking-wider mb-1" style={{color:'#7b96ff'}}>{post.category}</p>
              <h3 className="font-semibold text-white text-sm leading-tight line-clamp-2">{post.title}</h3>
              <p className="font-mono text-[10px] text-white/30 mt-1.5">
                {post.profiles?.username ?? 'Author'} · {format(parseISO(post.published_at), 'MMM d')}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── ChronologicalPage ────────────────────────────────────
export function ChronologicalPage() {
  const { data } = useEntries()
  const allEntries = data?.pages.flat() ?? []
  const byYear = allEntries.reduce((acc, e) => {
    const y = new Date(e.recorded_at).getFullYear()
    if (!acc[y]) acc[y] = []
    acc[y].push(e)
    return acc
  }, {} as Record<number, typeof allEntries>)
  const years = Object.keys(byYear).sort((a,b) => +a - +b)
  return (
    <div className="flex flex-col min-h-full">
      <div className="glass sticky top-0 z-20 px-5 pt-14 pb-4 safe-top flex items-center gap-3">
        <Link to="/timeline"><ChevronRight size={20} className="text-white/40 rotate-180" /></Link>
        <div>
          <h1 className="font-serif text-xl font-bold text-white">Chronological View</h1>
          <p className="font-mono text-[9px] tracking-widest uppercase" style={{color:'#7b96ff'}}>History in the Making</p>
        </div>
      </div>
      <div className="px-4 py-6 relative">
        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2"
          style={{background:'linear-gradient(to bottom,transparent,rgba(79,110,247,0.25),transparent)'}} />
        {years.length === 0 && (
          <div className="flex flex-col items-center py-20 gap-4">
            <p className="font-serif text-xl text-white/40 text-center">Add memories to see your chronological timeline.</p>
          </div>
        )}
        {years.map(year => (
          <div key={year} className="mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="relative z-10 px-4 py-1.5 rounded-xl border font-serif text-lg font-bold text-white"
                style={{background:'#131520',borderColor:'rgba(79,110,247,0.3)',boxShadow:'0 2px 16px rgba(79,110,247,0.15)'}}>
                {year}
              </div>
            </div>
            {byYear[+year].map((entry, i) => (
              <div key={entry.id} className={`flex gap-3 mb-4 ${i%2===0 ? 'flex-row' : 'flex-row-reverse'}`}>
                <div className="flex-1 card p-3">
                  {entry.media_urls?.[0] && (
                    <img src={entry.media_urls[0]} alt="" className="w-full h-24 object-cover rounded-lg mb-2" />
                  )}
                  <p className="font-mono text-[9px] text-white/30 mb-1">{format(parseISO(entry.recorded_at),'MMM d')}</p>
                  <p className="font-semibold text-white text-sm leading-tight">{entry.title}</p>
                  {entry.is_milestone && <p className="text-yellow-400 text-xs mt-1">🏆 Milestone</p>}
                </div>
                <div className="flex flex-col items-center gap-1 relative z-10">
                  <div className={`w-3 h-3 rounded-full mt-4 border-2 border-bg`}
                    style={{
                      background: entry.is_milestone ? '#f0b429' : '#4f6ef7',
                      boxShadow: entry.is_milestone ? '0 0 8px rgba(240,180,41,0.6)' : '0 0 8px rgba(79,110,247,0.6)',
                    }} />
                </div>
                <div className="flex-1" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── SettingsPage ─────────────────────────────────────────
export function SettingsPage() {
  const { signOut } = useAuthStore()
  const navigate = useNavigate()
  return (
    <div className="flex flex-col min-h-full">
      <div className="glass sticky top-0 z-20 px-5 pt-14 pb-4 safe-top">
        <h1 className="font-serif text-2xl font-bold text-white">Settings</h1>
      </div>
      <div className="px-4 py-4 flex flex-col gap-2">
        {[
          { label:'Privacy Controls', icon:'🔒', to:'/profile' },
          { label:'Legacy Mode', icon:'🕯️', to:'/legacy' },
          { label:'Legal Vault', icon:'🔐', to:'/vault' },
          { label:'Notifications', icon:'🔔', to:'/notifications' },
          { label:'Day Summary', icon:'🗓', to:'/summary' },
        ].map(item => (
          <Link key={item.label} to={item.to} className="card p-4 flex items-center gap-3">
            <span className="text-xl">{item.icon}</span>
            <span className="text-white font-medium flex-1">{item.label}</span>
            <ChevronRight size={16} className="text-white/30" />
          </Link>
        ))}
        <div className="mt-4 card p-4">
          <p className="font-mono text-[9px] text-white/20 tracking-widest uppercase mb-3">Our Promise</p>
          {['No ads in your timeline','No selling your data','No engagement algorithms','End-to-end encryption'].map(p => (
            <p key={p} className="text-white/40 text-sm py-1 flex items-center gap-2">
              <span className="text-green-400 text-xs">✓</span> {p}
            </p>
          ))}
        </div>
        <button
          onClick={async () => { await signOut(); navigate('/') }}
          className="mt-4 w-full py-3.5 rounded-2xl border font-semibold text-sm transition-all"
          style={{borderColor:'rgba(244,63,94,0.3)',color:'#f43f5e',background:'rgba(244,63,94,0.05)'}}>
          Sign Out
        </button>
        <p className="text-center font-mono text-[10px] text-white/20 mt-2">Timeline v0.1.0</p>
      </div>
    </div>
  )
}
